import React, { useEffect, useState } from "react"
import { Heart, Sparkles } from "lucide-react"
import { Message } from '../types'

interface CitationProps {
  message: Message;
}

export default function Citation({ message }: CitationProps) {
  const [mounted, setMounted] = useState(false)
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([])

  useEffect(() => {
    setMounted(true)

    // Générer des particules aléatoirement
    const newParticles = Array.from({ length:50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 4,
    }))
    setParticles(newParticles)
  }, [])

  if (!mounted) return null

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Gradient animé en arrière-plan */}
      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-cyan-500/20 animate-pulse" />

      {/* Particules flottantes */}
      <div className="absolute inset-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-bounce"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Cercles décoratifs animés */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse" />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      {/* Contenu principal */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Icône décorative */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <Sparkles className="w-12 h-12 text-yellow-400 animate-spin" style={{ animationDuration: "3s" }} />
              <Heart className="w-6 h-6 text-pink-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>

          {/* Citation principale */}
          <div className="relative">
            {/* Effet de lueur derrière le texte */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-cyan-500/20 blur-3xl rounded-3xl" />

            <blockquote className="relative">
              <div className="text-6xl text-pink-300/50 font-serif mb-4">"</div>

              <p className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-6 tracking-wide">
                <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-pulse">
                  {message.message}
                </span>
              </p>

              <div className="text-6xl text-cyan-300/50 font-serif rotate-180">"</div>
            </blockquote>
          </div>

          {/* Signature */}
          <div className="mt-12">
            <div className="inline-block px-8 py-4 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <p className="text-xl text-white/80 font-medium">Avec tout mon amour ❤️</p>
            </div>
          </div>

          {/* Effets décoratifs supplémentaires */}
          <div className="mt-16 flex justify-center space-x-8">
            <div className="w-2 h-2 bg-pink-400 rounded-full animate-ping" />
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: "0.5s" }} />
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-ping" style={{ animationDelay: "1s" }} />
          </div>
        </div>
      </div>

      {/* Overlay subtil */}
      <div className="absolute inset-0 bg-black/20" />
    </div>
  )
}
